import React, { createContext, useContext, useRef, useState } from 'react';
import { noop } from 'lodash';
import { Popover } from 'syngenta-digital-cropwise-react-ui-kit';
import './index.css';

// Items close the menu through this ref: the ref object keeps one identity,
// so the provider's value never changes between renders.
const CloseContext = createContext<React.RefObject<() => void>>({
  current: noop
});

type PopupMenuProps = {
  trigger: React.ReactElement;
  children: React.ReactNode;
  placement?: 'topLeft' | 'topRight';
  // Controlled use; leave both out to let the menu manage itself.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

/**
 * A click-to-open menu above its trigger, replacing grow-ui's Popup. The
 * panel renders inside the trigger's wrapper and is at least as wide as it,
 * as grow-ui's was.
 */
const PopupMenu = ({
  trigger,
  children,
  placement = 'topLeft',
  open,
  onOpenChange,
  className = ''
}: PopupMenuProps) => {
  const [ownOpen, setOwnOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const isOpen = open ?? ownOpen;
  const closeRef = useRef<() => void>(noop);

  const setOpen = (next: boolean) => {
    if (open === undefined) {
      setOwnOpen(next);
    }
    onOpenChange?.(next);
  };
  closeRef.current = () => setOpen(false);

  return (
    <span ref={wrapperRef} className={`relative inline-block ${className}`}>
      <Popover
        open={isOpen}
        onOpenChange={setOpen}
        trigger="click"
        placement={placement}
        showArrow={false}
        // grow-ui unmounted the panel on close.
        destroyTooltipOnHide
        align={{ offset: [0, -6] }}
        overlayClassName="cwf-kit-popup-menu"
        overlayStyle={{ minWidth: '100%' }}
        getPopupContainer={() => wrapperRef.current ?? document.body}
        content={
          <CloseContext.Provider value={closeRef}>
            {children}
          </CloseContext.Provider>
        }
      >
        {/* A DOM element, so the popover can anchor to it: the kit Button is
            a class component and cannot take a ref. */}
        <span className="inline-block w-full">{trigger}</span>
      </Popover>
    </span>
  );
};

type ListProps = {
  children: React.ReactNode;
};

const List = ({ children }: ListProps) => (
  <ul className="cwf-kit-popup-menu__list">{children}</ul>
);

type ItemProps = {
  onClick: () => void;
  children: React.ReactNode;
  'data-testid'?: string;
};

const Item = ({ onClick, children, 'data-testid': dataTestId }: ItemProps) => {
  const closeRef = useContext(CloseContext);
  return (
    <li className="cwf-kit-popup-menu__item">
      <button
        type="button"
        data-testid={dataTestId}
        onClick={() => {
          closeRef.current();
          onClick();
        }}
      >
        {children}
      </button>
    </li>
  );
};

PopupMenu.List = List;
PopupMenu.Item = Item;

export default PopupMenu;
